import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import AcCanvas3D from "../components/AcCanvas3D";
import { BrandMarqueeDark, BrandMarqueeLight } from "../components/BrandMarquee";
import TrustBadges from "../components/TrustBadges";
import ProductCard from "../components/ProductCard";
import CountdownTimer from "../components/CountdownTimer";
import { api } from "../lib/api";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useLang } from "../context/LanguageContext";
import { resolveImg, resolveImgSized } from "../lib/api";

export default function Home() {
  // "lightCommercial" = cassette + floor + duct AC products (replaces the
  // old "Featured this season" editorial strip). These are the higher-load
  // units aimed at offices / shops / showrooms — a distinct shopping path
  // from the residential splits shown elsewhere on the home page.
  const [lightCommercial, setLightCommercial] = useState([]);
  const [deals, setDeals] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [stats, setStats] = useState({});
  const [ratings, setRatings] = useState({});
  const [aiPro, setAiPro] = useState(null);
  const { settings } = useSiteSettings() || {};
  const { lang, t, tType } = useLang();
  const hero = settings?.hero || {};
  const sections = settings?.sections || {};

  // Default fallback categories (i18n-aware)
  const FALLBACK_TYPES = [
    { key: "split", label: tType("split") || t("nav.split"), image: "" },
    { key: "cassette", label: tType("cassette") || t("nav.cassette"), image: "" },
    { key: "floor", label: tType("floor") || t("nav.floor"), image: "" },
    { key: "duct", label: tType("duct") || t("nav.duct"), image: "" },
  ];
  const categories = (settings?.categories?.length ? settings.categories : FALLBACK_TYPES).map((c) => ({
    key: c.key,
    // Always prefer the universal type dictionary (tType) so Home, Shop filters
    // and header nav stay perfectly consistent. Fall back to admin label then key.
    label: lang === "ar"
      ? (tType(c.key) || c.label_ar || c.label || c.key)
      : (c.label || c.key),
    img: c.image || c.img,
  }));

  // Bilingual picker: prefer _ar field when in Arabic, otherwise use admin CMS value.
  // In AR mode: if no admin AR override is set, prefer the i18n translation over leaving English text.
  const txt = (cmsVal, key, arVal) => {
    if (lang === "ar") {
      if (arVal) return arVal;
      const translated = t(key);
      // When translation exists (not equal to key), prefer it over raw English CMS value.
      if (translated && translated !== key) return translated;
      if (cmsVal) return cmsVal;
      return translated;
    }
    return cmsVal || t(key);
  };

  useEffect(() => {
    // For each home strip, merge admin-tagged products (shown first) with the
    // auto rule fallback (newest / bestseller / on_sale / type) so a partial
    // curation doesn't hide everything else.
    const fetchSection = async (section, autoFallback, max = 6) => {
      const [taggedRes, fallback] = await Promise.allSettled([
        api.get("/products", { params: { section, limit: max } }),
        autoFallback(),
      ]);
      const tagged = taggedRes.status === "fulfilled" ? taggedRes.value.data : [];
      const auto = fallback.status === "fulfilled" ? fallback.value : [];
      const seen = new Set(tagged.map((p) => p.id));
      const merged = [...tagged];
      for (const p of auto) {
        if (merged.length >= max) break;
        if (!seen.has(p.id)) {
          seen.add(p.id);
          merged.push(p);
        }
      }
      return merged.slice(0, max);
    };

    fetchSection("light_commercial", async () => {
      const [c, f, d] = await Promise.all([
        api.get("/products", { params: { type: "cassette", limit: 4 } }),
        api.get("/products", { params: { type: "floor", limit: 4 } }),
        api.get("/products", { params: { type: "duct", limit: 4 } }),
      ]);
      const maxLen = Math.max(c.data.length, f.data.length, d.data.length);
      const interleaved = [];
      for (let i = 0; i < maxLen; i++) {
        if (c.data[i]) interleaved.push(c.data[i]);
        if (f.data[i]) interleaved.push(f.data[i]);
        if (d.data[i]) interleaved.push(d.data[i]);
      }
      return interleaved.slice(0, 6);
    }, 6).then(setLightCommercial).catch(() => setLightCommercial([]));

    fetchSection("deals",
      async () => (await api.get("/products", { params: { on_sale: true, limit: 8 } })).data,
      8,
    ).then(setDeals).catch(() => setDeals([]));

    fetchSection("new_arrivals",
      async () => (await api.get("/products", { params: { sort: "newest", limit: 6 } })).data,
      6,
    ).then(setNewArrivals).catch(() => setNewArrivals([]));

    fetchSection("best_sellers",
      async () => (await api.get("/products", { params: { sort: "bestseller", limit: 6 } })).data,
      6,
    ).then(setBestSellers).catch(() => setBestSellers([]));

    api.get("/category-stats").then((r) => setStats(r.data || {}));
    api.get("/ratings").then((r) => setRatings(r.data || {}));
    api
      .get("/products/comfee-xpet-AI-pro")
      .then((r) => setAiPro(r.data))
      .catch((err) => {
        // Non-fatal: hero falls back to the AI Pro placeholder if the API is unreachable.
        console.warn("Failed to load AI Pro hero product:", err);
        setAiPro(null);
      });
  }, []);

  // Hero product specs (safe even before API resolves)
  const aiProSpecs = aiPro?.specs || {};
  // Highlight the entry-level (lowest BTU / cheapest) variant in the hero price.
  // Falls back to top-level product price if no variants.
  const aiProVariants = aiPro?.variants || [];
  const entryVariant = aiProVariants.length
    ? [...aiProVariants].sort((a, b) => Number(a.btu || 0) - Number(b.btu || 0))[0]
    : null;
  const aiProPrice = entryVariant
    ? (entryVariant.sale_price || entryVariant.price)
    : (aiPro?.sale_price || aiPro?.price);
  const aiProOriginal = entryVariant
    ? (entryVariant.sale_price ? entryVariant.price : null)
    : (aiPro?.sale_price ? aiPro?.price : null);
  const currency = settings?.general?.currency_symbol || "JOD";
  const warrantyYearsMatch = String(aiProSpecs.warranty || "").match(/\d+/);
  const warrantyYears = warrantyYearsMatch ? warrantyYearsMatch[0] : null;

  return (
    <div data-testid="home-page">
      {/* HERO — FLAGSHIP: COMFEE XPERT AI PRO */}
      <section className="relative bg-[#FF7A1A] text-ink overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6"
          >
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-ink/80 bg-ink/10 border border-ink/20 rounded-full px-3 py-1 mb-5"
            >
              <span className="inline-block w-1.5 h-1.5 bg-ink rounded-full" />
              {t("home.hero.flagship")}
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display font-black text-[2.5rem] sm:text-6xl lg:text-[5rem] leading-[0.95] tracking-tighter"
            >
              {t("home.hero.aiproHeadline1")}
              <br />
              <span className="text-white drop-shadow-[0_2px_0_rgba(0,0,0,0.08)]">
                {t("home.hero.aiproHeadline2")}
              </span>
              <br />
              {t("home.hero.aiproHeadline3")}
            </h1>
            <p className="mt-6 max-w-lg text-base text-ink/85 leading-relaxed">
              {t("home.hero.aiproBlurb")}
            </p>

            {/* Spec chips */}
            <div data-testid="hero-specs" className="mt-7 flex flex-wrap gap-2">
              {(entryVariant?.btu || aiProSpecs.btu) && (
                <span className="inline-flex items-baseline gap-1.5 bg-ink text-white px-3 py-1.5 text-xs font-mono uppercase tracking-widest">
                  <span className="font-display font-black text-sm">{entryVariant?.btu || aiProSpecs.btu}</span>
                  <span className="opacity-70">{t("home.hero.spec.btu")}</span>
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 bg-white text-ink px-3 py-1.5 text-xs font-mono uppercase tracking-widest border border-ink/10">
                <span className="w-1.5 h-1.5 bg-electric rounded-full" />
                {t("home.hero.spec.inverter")}
              </span>
              {aiProSpecs.energy_rating && (
                <span className="inline-flex items-baseline gap-1.5 bg-white text-ink px-3 py-1.5 text-xs font-mono uppercase tracking-widest border border-ink/10">
                  <span className="font-display font-black text-sm">{aiProSpecs.energy_rating}</span>
                  <span className="opacity-60">{t("home.hero.spec.energy")}</span>
                </span>
              )}
              {warrantyYears && (
                <span className="inline-flex items-baseline gap-1.5 bg-white text-ink px-3 py-1.5 text-xs font-mono uppercase tracking-widest border border-ink/10">
                  <span className="font-display font-black text-sm">
                    {warrantyYears} {t("home.hero.spec.years")}
                  </span>
                  <span className="opacity-60">{t("home.hero.spec.warranty")}</span>
                </span>
              )}
            </div>

            {/* Price + CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              {aiPro?.quote_only ? (
                <div
                  data-testid="hero-price"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white text-xs font-mono uppercase tracking-[0.2em]"
                >
                  <span className="inline-block w-1.5 h-1.5 bg-electric rounded-full" />
                  {t("common.priceOnRequest")}
                </div>
              ) : (
                aiProPrice && (
                  <div data-testid="hero-price" className="flex items-baseline gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-ink/60">
                      {entryVariant ? entryVariant.label : t("home.hero.priceFrom")}
                    </span>
                    <span className="font-display font-black text-3xl">
                      {aiProPrice} {currency}
                    </span>
                    {aiProOriginal && (
                      <span className="text-sm font-mono text-ink/50 line-through">
                        {aiProOriginal} {currency}
                      </span>
                    )}
                  </div>
                )
              )}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to="/product/comfee-xpet-AI-pro"
                data-testid="hero-view-aipro"
                className="inline-flex items-center gap-2 h-12 px-6 bg-ink text-white hover:bg-black transition-colors text-xs font-mono uppercase tracking-[0.2em]"
              >
                {t("home.hero.viewAiPro")} <ArrowRight size={16} className="rtl-flip" />
              </Link>
              <Link
                to="/shop"
                data-testid="hero-browse-all"
                className="inline-flex items-center gap-2 h-12 px-6 border-2 border-ink hover:bg-ink hover:text-white transition-colors text-xs font-mono uppercase tracking-[0.2em]"
              >
                {t("home.hero.browseAll")}
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-6"
          >
            <Link to="/product/comfee-xpet-AI-pro" aria-label={t("home.hero.viewAiPro")} className="block">
              <AcCanvas3D />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE (dark) */}
      <BrandMarqueeDark />

      {/* TRUST BADGES */}
      <TrustBadges />

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">{txt(sections.categories_eyebrow, "home.categories.eyebrow", sections.categories_eyebrow_ar)}</div>
            <h2 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {txt(sections.categories_title, "home.categories.title", sections.categories_title_ar)}
            </h2>
          </div>
          <Link to="/shop" className="text-sm font-mono uppercase tracking-widest border-b border-ink pb-1 hover:text-electric hover:border-electric">
            {t("home.categories.viewAll")}
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map((c) => (
            <Link
              key={c.key}
              to={`/shop?type=${c.key}`}
              data-testid={`category-${c.key}`}
              className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-white shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_60px_-20px_rgba(0,85,255,0.3)] transition-shadow duration-500"
            >
              <img
                src={resolveImgSized(c.img, 600)}
                alt={c.label}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-contain p-8 group-hover:scale-110 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
              />
              <div className="absolute top-4 left-4 z-10">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-electric">
                  {t("home.categories.label")}
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
                <div className="font-display font-extrabold text-2xl text-ink leading-none">{c.label}</div>
                <span className="w-9 h-9 rounded-full bg-ink text-white grid place-items-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  <ArrowRight size={14} className="rtl-flip" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* MARQUEE (light, under categories) */}
      <BrandMarqueeLight />

      {/* DEALS OF THE DAY */}
      <section className="bg-secondary py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-electric">{txt(sections.deals_eyebrow, "home.deals.eyebrow", sections.deals_eyebrow_ar)}</div>
              <h2 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                {txt(sections.deals_title, "home.deals.title", sections.deals_title_ar)}
              </h2>
            </div>
            <CountdownTimer hours={36} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {deals.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} rating={ratings[p.slug]} />
            ))}
          </div>
        </div>
      </section>

      {/* LIGHT COMMERCIAL — replaces the old "Featured this season" strip. */}
      <section data-testid="home-light-commercial" className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">
              {t("home.lightCommercial.eyebrow")}
            </div>
            <h2 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {t("home.lightCommercial.title")}
            </h2>
            <p className="mt-3 text-slate-600 max-w-xl">
              {t("home.lightCommercial.subtitle")}
            </p>
          </div>
          <Link
            to="/shop?category=commercial"
            className="text-sm font-mono uppercase tracking-widest border-b border-ink pb-1 hover:text-electric hover:border-electric"
          >
            {t("home.featured.viewAll")}
          </Link>
        </div>
        {lightCommercial.length === 0 ? (
          <div className="text-sm text-slate-500 font-mono">
            No commercial units listed yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {lightCommercial.map((p) => (
              <ProductCard key={p.id} product={p} rating={ratings[p.slug]} />
            ))}
          </div>
        )}
      </section>

      {/* NEW ARRIVALS */}
      {newArrivals.length > 0 && (
        <section data-testid="home-new-arrivals" className="max-w-[1400px] mx-auto px-6 md:px-12 pb-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-electric">
                {t("home.newArrivals.eyebrow")}
              </div>
              <h2 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
                {t("home.newArrivals.title")}
              </h2>
            </div>
            <Link to="/shop" className="text-sm font-mono uppercase tracking-widest border-b border-ink pb-1 hover:text-electric hover:border-electric">
              {t("home.featured.viewAll")}
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {newArrivals.slice(0, 6).map((p) => (
              <ProductCard key={p.id} product={p} rating={ratings[p.slug]} />
            ))}
          </div>
        </section>
      )}

      {/* BEST SELLERS */}
      {bestSellers.length > 0 && (
        <section data-testid="home-best-sellers" className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF7A1A]">
                {t("home.bestSellers.eyebrow")}
              </div>
              <h2 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
                {t("home.bestSellers.title")}
              </h2>
            </div>
            <Link to="/shop" className="text-sm font-mono uppercase tracking-widest border-b border-ink pb-1 hover:text-electric hover:border-electric">
              {t("home.featured.viewAll")}
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {bestSellers.slice(0, 6).map((p) => (
              <ProductCard key={p.id} product={p} rating={ratings[p.slug]} />
            ))}
          </div>
        </section>
      )}

      {/* CTA Strip */}
      <section className="bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
            {txt(sections.cta_strip_text, "home.cta.text", sections.cta_strip_text_ar)}
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
            <Link
              to="/shop"
              className="h-12 px-6 inline-flex items-center justify-center bg-electric text-white text-xs font-mono uppercase tracking-[0.2em] hover:bg-electricHover transition-colors"
            >
              {txt(sections.cta_strip_button_label, "home.cta.button", sections.cta_strip_button_label_ar)}
            </Link>
            <a
              href={`tel:${(settings?.general?.phone || "+962799448817").replace(/\s/g, "")}`}
              className="h-12 px-6 inline-flex items-center justify-center border border-white text-xs font-mono uppercase tracking-[0.2em] hover:bg-white hover:text-ink transition-colors"
            >
              {t("home.cta.call")} {settings?.general?.phone || "+962 79 944 8817"}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
